import NoAccountsIcon from "@mui/icons-material/NoAccounts";
import { Avatar, Box, Button, Chip, Divider, Typography } from "@mui/material";
import React, { useState } from "react";
import swal from "sweetalert";
import CustomTable from "../../components/CustomTable";
import MainLayout from "../../components/MainLayout";
import {
  LeftPane,
  RightPane,
  SplitViewContainer,
} from "../../components/SplitView";
import axiosInstance from "../../utils/axiosInstance";
import { checkPermission } from "../../utils/checkPermission";
import {
  capitalizeFirstLetter,
  formatCustomDate,
  formatCustomDateTime,
} from "../../utils/formatters";
import theme from "../../utils/theme";

export default function Support() {
  const [refresh, setRefresh] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const columns = [
    {
      field: "profile_picture",
      headerName: "Avatar",
      width: 70,
      type: "actions",
      renderCell: (value) => (
        <Box>
          {value.row.user ? (
            <Avatar
              src={`${process.env.REACT_APP_STORAGE_URL}/${value.row.user.profile_picture}`}
              alt={`${value.row.user.full_name.slice(0).toUpperCase()}`}
              sx={{ width: "24px", height: "24px" }}
            />
          ) : (
            <Avatar sx={{ width: "24px", height: "24px" }}>
              <NoAccountsIcon />
            </Avatar>
          )}
        </Box>
      ),
    },
    {
      field: "user",
      headerName: "user",
      width: 300,
      renderCell: (value) => {
        if (value.row.user) {
          return `${value.row.user.full_name}`;
        } else {
          return <i>Anonymous User</i>;
        }
      },
    },
    { field: "title", headerName: "Title", width: 300 },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      valueGetter: (value) => capitalizeFirstLetter(value),
      type: "singleSelect",
      valueOptions: ["Open", "In-progress", "Closed"],
      sortable: false,
    },
    {
      field: "support_category",
      headerName: "Support Category",
      width: 200,
      renderCell: (value) => {
        return capitalizeFirstLetter(value.row.support_category.name);
      },
      sortable: false,
    },
    {
      field: "created_at",
      headerName: "Created At",
      width: 140,
      valueGetter: (value) => value && new Date(value),
      renderCell: (value) => formatCustomDate(value.row.created_at),
      type: "date",
    },
    {
      field: "",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      type: "actions",
      renderCell: (value) => (
        <Box>
          <Button
            color="info"
            onClick={() => {
              setSelectedRequest(value.row);
            }}
          >
            View Details
          </Button>
        </Box>
      ),
    },
  ];

  const handleStatusChange = async (status) => {
    try {
      const newStatus = status;
      const response = await axiosInstance.patch(
        `${process.env.REACT_APP_API_URL}/support/${selectedRequest.id}`,
        {
          status: newStatus,
        },
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        setSelectedRequest((prevRequest) => ({
          ...prevRequest,
          status: newStatus,
        }));
        swal("Successfully changed the selected request status", {
          icon: "success",
        });
      }
    } catch (error) {
      swal("Failed to change status of support request. Please try again.", {
        icon: "error",
      });
    }
  };

  return (
    <MainLayout>
      <Typography variant="h6">Support</Typography>

      <SplitViewContainer theme={theme}>
        <LeftPane theme={theme}>
          <CustomTable
            columns={columns}
            fetchDataUrl="support-paginated"
            refresh={refresh}
          />
        </LeftPane>

        <Divider orientation="vertical" />

        <RightPane theme={theme}>
          {selectedRequest ? (
            <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
              <Typography>{`Opened by ${selectedRequest.user ? selectedRequest.user.full_name : "Anonymous User"} on ${formatCustomDateTime(selectedRequest.created_at)}`}</Typography>
              <Box>
                <Chip label={capitalizeFirstLetter(selectedRequest.status)} />
                <Typography variant="h5">{selectedRequest.title}</Typography>
                <Typography sx={{ fontSize: "18px" }}>
                  {selectedRequest.description}
                </Typography>
              </Box>
              {/* User contact info */}
              <Typography variant="h6">Contact Information</Typography>
              {selectedRequest.user ? (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "rwo",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Typography>User:</Typography>
                    {selectedRequest.user ? (
                      <Avatar
                        src={`${process.env.REACT_APP_STORAGE_URL}/${selectedRequest.user.profile_picture}`}
                        alt={`${selectedRequest.user.full_name.slice(0).toUpperCase()}`}
                        sx={{ width: 24, height: 24 }}
                      />
                    ) : (
                      <Avatar>
                        <NoAccountsIcon />
                      </Avatar>
                    )}
                    <Typography>{selectedRequest.user.full_name}</Typography>
                  </Box>
                  <Typography>{`Email: ${selectedRequest.user.email ? selectedRequest.user.email : "N/A"}`}</Typography>
                  <Typography>{`Phone Number: ${selectedRequest.user.phone_number ? selectedRequest.user.phone_number : "N/A"}`}</Typography>
                </Box>
              ) : (
                <Typography fontStyle="italic">
                  This request was opened by anonymous user.
                </Typography>
              )}
              {checkPermission("support-update") && (
                <>
                  <Typography variant="h6">Actions</Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    {selectedRequest.status !== "open" && (
                      <Button
                        variant="contained"
                        onClick={() => handleStatusChange("open")}
                      >
                        Change Status to Open
                      </Button>
                    )}
                    {selectedRequest.status !== "in-progress" && (
                      <Button
                        variant="contained"
                        onClick={() => handleStatusChange("in-progress")}
                      >
                        Change Status to In-progress
                      </Button>
                    )}
                    {selectedRequest.status !== "closed" && (
                      <Button
                        variant="contained"
                        onClick={() => handleStatusChange("closed")}
                      >
                        Change Status to Closed
                      </Button>
                    )}
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                width: "100%",
              }}
            >
              <Typography variant="body2">
                Select one support request to view details
              </Typography>
            </Box>
          )}
        </RightPane>
      </SplitViewContainer>
    </MainLayout>
  );
}
